import { AppSettings } from '../types';

export interface SlackBlockPayload {
    text?: string;
    blocks?: any[];
    attachments?: any[];
}

/**
 * Sends a raw payload to a Slack Webhook URL
 */
export const sendSlackNotification = async (webhookUrl: string, payload: SlackBlockPayload): Promise<boolean> => {
    if (!webhookUrl || !webhookUrl.startsWith('https://hooks.slack.com/')) {
        console.warn('Slack Integration: Invalid Webhook URL');
        return false;
    }

    try {
        const response = await fetch(webhookUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(payload)
        });
        return response.ok || response.status === 200;
    } catch (error) {
        console.error('Failed to send Slack notification:', error);
        return false;
    }
};

/**
 * Sends a live test message to verify the manager's Slack Webhook connection
 */
export const sendSlackTestMessage = async (webhookUrl: string): Promise<boolean> => {
    const payload: SlackBlockPayload = {
        text: '🎉 KithHR Slack Integration Connected Successfully!',
        blocks: [
            {
                type: 'header',
                text: {
                    type: 'plain_text',
                    text: '🎉 KithHR Connected to Slack!',
                    emoji: true
                }
            },
            {
                type: 'section',
                text: {
                    type: 'mrkdwn',
                    text: 'Your KithHR account has been successfully connected to this channel. You will now receive live alerts for clock-ins, clock-outs, out-of-range flags, and time-off requests!'
                }
            },
            {
                type: 'context',
                elements: [
                    {
                        type: 'mrkdwn',
                        text: `*Status:* Active • *Timestamp:* ${new Date().toLocaleString()} • *App:* KithHR v1.0.4`
                    }
                ]
            }
        ]
    };

    return sendSlackNotification(webhookUrl, payload);
};

/**
 * Notify Slack channel when an employee clocks in
 */
export const notifySlackClockIn = async (
    settings: AppSettings,
    employeeName: string,
    position?: string,
    locationName?: string
): Promise<void> => {
    if (!settings.slackEnabled || !settings.slackWebhookUrl || settings.slackNotifyClockInOut === false) return;

    const payload: SlackBlockPayload = {
        text: `🟢 ${employeeName} clocked in`,
        blocks: [
            {
                type: 'section',
                text: {
                    type: 'mrkdwn',
                    text: `🟢 *${employeeName}* clocked in.`
                }
            },
            {
                type: 'context',
                elements: [
                    {
                        type: 'mrkdwn',
                        text: `*Position:* ${position || 'Staff'} | *Time:* ${new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}${locationName ? ` | *Location:* ${locationName}` : ''}`
                    }
                ]
            }
        ]
    };

    await sendSlackNotification(settings.slackWebhookUrl, payload);
};

/**
 * Notify Slack channel when an employee clocks out
 */
export const notifySlackClockOut = async (
    settings: AppSettings,
    employeeName: string,
    durationFormatted: string
): Promise<void> => {
    if (!settings.slackEnabled || !settings.slackWebhookUrl || settings.slackNotifyClockInOut === false) return;

    const payload: SlackBlockPayload = {
        text: `🔴 ${employeeName} clocked out (${durationFormatted})`,
        blocks: [
            {
                type: 'section',
                text: {
                    type: 'mrkdwn',
                    text: `🔴 *${employeeName}* clocked out.`
                }
            },
            {
                type: 'context',
                elements: [
                    {
                        type: 'mrkdwn',
                        text: `*Payable Time Today:* ${durationFormatted} | *Time:* ${new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
                    }
                ]
            }
        ]
    };

    await sendSlackNotification(settings.slackWebhookUrl, payload);
};

/**
 * Notify Slack channel when a Geofence / Location Bypass occurs
 */
export const notifySlackGeofenceBypass = async (
    settings: AppSettings,
    employeeName: string,
    type: 'In' | 'Out',
    reason: string
): Promise<void> => {
    if (!settings.slackEnabled || !settings.slackWebhookUrl || settings.slackNotifyGeofenceBypass === false) return;

    const payload: SlackBlockPayload = {
        text: `⚠️ Out of Range Flag: ${employeeName} clocked ${type.toLowerCase()}`,
        blocks: [
            {
                type: 'section',
                text: {
                    type: 'mrkdwn',
                    text: `⚠️ *OUT OF RANGE FLAG (Clock ${type})*\n*Employee:* ${employeeName}\n*Reason Given:* _"${reason}"_`
                }
            },
            {
                type: 'context',
                elements: [
                    {
                        type: 'mrkdwn',
                        text: `*Requires Review in KithHR Timesheets* | ${new Date().toLocaleString()}`
                    }
                ]
            }
        ]
    };

    await sendSlackNotification(settings.slackWebhookUrl, payload);
};

/**
 * Notify Slack channel when a Time-Off / Holiday Request is submitted
 */
export const notifySlackTimeOff = async (
    settings: AppSettings,
    employeeName: string,
    dates: string,
    reason?: string
): Promise<void> => {
    if (!settings.slackEnabled || !settings.slackWebhookUrl || settings.slackNotifyTimeOff === false) return;

    const payload: SlackBlockPayload = {
        text: `🌴 Time-Off Request: ${employeeName}`,
        blocks: [
            {
                type: 'section',
                text: {
                    type: 'mrkdwn',
                    text: `🌴 *NEW TIME-OFF REQUEST*\n*Employee:* ${employeeName}\n*Dates:* ${dates}${reason ? `\n*Reason:* _"${reason}"_` : ''}`
                }
            },
            {
                type: 'context',
                elements: [
                    {
                        type: 'mrkdwn',
                        text: `*Status:* Pending Manager Approval in KithHR`
                    }
                ]
            }
        ]
    };

    await sendSlackNotification(settings.slackWebhookUrl, payload);
};
